import { Component, OnInit } from '@angular/core';
import { SubscriptionBasedComponent } from '../../../subscription-helpers/subscription-based.component';
import { AtelierAssignmentTour } from './model/atelier-assignment-tour';
import { AtelierControllerService } from '../service/atelier-controller.service';
import { AtelierService } from '../../../services/atelier-service';
import { AtelierUpdateCheckedResponse } from '../atelier-detail/model/atelier-update-checked-response';
import { ActivatedRoute } from '@angular/router';
import { CompletedAssignmentModalComponent } from '../../../shared/completed-assignment-modal/completed-assignment-modal.component';
import {
  ModalService,
  ObservableHandlerPair,
} from '../../../services/modal.service';
import { User } from '../../../model/user';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'atelier-assignments-view',
  templateUrl: './atelier-assignments-view.component.html',
  styleUrls: ['./atelier-assignments-view.component.scss'],
})
export class AtelierAssignmentsViewComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  tourAssignments: AtelierAssignmentTour[];
  loadingAssignments = false;

  selectedTourId: number;

  reviewOnly: boolean = false;
  user: User;

  constructor(
    private atelierControllerService: AtelierControllerService,
    private atelierService: AtelierService,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.addSubscription(
      this.route.data,
      (data) => (this.reviewOnly = data.reviewOnly)
    );
    const earlierStoredDate = this.atelierService
      .getSelectedAtelierDateSubscribable()
      .getValue();
    if (earlierStoredDate == null) {
      this.atelierService.setSelectedAtelierDate(new Date());
    }
    this.fetchAssignments(
      this.atelierService.getSelectedAtelierDateSubscribable().getValue()
    );

    this.addSubscription(
      this.atelierService.getSelectedAtelierDateSubscribable(),
      (date) => this.fetchAssignments(date)
    );

    this.addSubscription(
      this.atelierService.getSelectedAtelierTourSubscribable(),
      (earlierStoredTourID) => {
        this.selectedTourId = earlierStoredTourID;
      }
    );

    this.user = this.authService.getAuthenticatedUser();
  }

  fetchAssignments(date: Date) {
    this.loadingAssignments = true;
    this.atelierControllerService
      .myAssignments(date, this.reviewOnly)
      .then(
        (tourAssignments) =>
          (this.tourAssignments = this.parseTourAssignments(tourAssignments))
      )
      .finally(() => (this.loadingAssignments = false));
  }

  private parseTourAssignments(
    tourAssignments: AtelierAssignmentTour[]
  ): AtelierAssignmentTour[] {
    tourAssignments.forEach((tour) => {
      this.setTourStatus(tour);
    });

    return tourAssignments;
  }

  private setTourStatus(tour: AtelierAssignmentTour): void {
    if (tour.atelierFinished && tour.atelierReviewed) {
      tour.statusText = 'Afgewerkt + controle';
      tour.executedClass = 'finished-reviewed';
    } else if (tour.atelierFinished) {
      tour.statusText = 'Afgewerkt';
      tour.executedClass = 'finished';
    } else if (tour.atelierInProgress) {
      tour.statusText = 'Bezig';
      tour.executedClass = 'in-progress';
    } else {
      tour.statusText = '-';
      tour.executedClass = undefined;
    }
  }

  openTask(tour: AtelierAssignmentTour): void {
    if (this.selectedTourId === tour.id) {
      this.atelierService.setSelectedAtelierTour(undefined);
    } else {
      this.atelierService.setSelectedAtelierTour(tour.id);
    }
  }

  onCheckUpdate(response: AtelierUpdateCheckedResponse): void {
    const tour = this.tourAssignments.find(
      (tour) => tour.id === response.tourId
    );
    if (tour == null) {
      return; // This should never happen
    }

    // Update the specific tour's status
    tour.atelierFinished = response.finished;
    tour.atelierInProgress = response.inProgress;
    tour.atelierReviewed = response.reviewed;
    this.setTourStatus(tour);

    this.runFinishedCheck();
  }

  private runFinishedCheck() {
    // Check if all tours are finished or reviewed based on reviewOnly flag
    const allToursCompleted = this.reviewOnly
      ? this.tourAssignments.every((tour) => tour.atelierReviewed)
      : this.tourAssignments.every((tour) => tour.atelierFinished);

    if (allToursCompleted) {
      this.openCompletedTasksDialog();
    }
  }

  openCompletedTasksDialog() {
    this.modalService.openComponentInModal(
      CompletedAssignmentModalComponent,
      [
        {
          key: 'user',
          value: this.user,
        },
        {
          key: 'message',
          value:
            'Alle ' +
            (this.reviewOnly ? 'controles' : 'opdrachten') +
            ' afgewerkt.',
        },
        {
          key: 'orangeThumbsUp',
          value: true,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.afterClosed(),
        () => {}
      ),
      { width: '450px', maxHeight: '300px' }
    );
  }
}
